import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../../Navbar';
import FooterPart from '../../Footer';
import useMobileCheck from '../../useMobileCheck';

const API_URL = process.env.REACT_APP_API_URL;

const CHIP_COLORS = ['#1e293b', '#7c3aed', '#16a34a', '#d97706', '#2563eb', '#0f766e', '#b91c1c'];

function getChipColor(index) {
  return CHIP_COLORS[index % CHIP_COLORS.length];
}

function buildClassColors(entries) {
  const palette = ['#16a34a', '#2563eb', '#7c3aed', '#d97706', '#0f766e', '#b91c1c'];
  const map = {};
  let i = 0;
  entries.forEach((e) => {
    if (e.Klasa && !map[e.Klasa]) {
      map[e.Klasa] = palette[i % palette.length];
      i++;
    }
  });
  return map;
}

// ── Competition filter chips ──────────────────────────────────────────────────
function CompetitionChips({ competitions, selected, onChange }) {
  return (
    <div
      style={{
        margin: '20px 24px 0',
        padding: 14,
        background: '#fafafa',
        border: '1px solid #f4f4f5',
        borderRadius: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#71717a',
            letterSpacing: 0.5,
            flexShrink: 0,
          }}
        >
          ZAWODY
        </span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {competitions.map((comp, idx) => (
            <Chip
              key={comp.ID}
              label={comp.Name}
              count={comp.listCount}
              active={selected === comp.ID}
              color={getChipColor(idx)}
              onClick={() => onChange(comp.ID)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Chip({ label, count, active, color, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        border: '1px solid ' + (active ? color : '#e4e4e7'),
        borderRadius: 8,
        padding: '6px 11px',
        fontSize: 12,
        fontWeight: 700,
        background: active ? color : '#fff',
        color: active ? '#fff' : '#27272a',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        fontFamily: 'inherit',
      }}
    >
      {label}
      {count != null && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            padding: '1px 6px',
            borderRadius: 10,
            background: active ? 'rgba(255,255,255,0.25)' : '#f4f4f5',
            color: active ? '#fff' : '#52525b',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ── List name dropdown ────────────────────────────────────────────────────────
function ListDropdown({ lists, selectedId, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const safeList = Array.isArray(lists) ? lists : [];
  const current = safeList.find((l) => l.ID === selectedId);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div style={{ padding: '18px 24px 8px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#71717a',
          letterSpacing: 0.5,
          flexShrink: 0,
        }}
      >
        LISTA STARTOWA
      </span>
      <div style={{ position: 'relative' }} ref={ref}>
        <button
          onClick={() => setOpen((o) => !o)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#fff',
            border: '1px solid #e4e4e7',
            borderRadius: 8,
            padding: '6px 10px',
            fontSize: 13,
            fontWeight: 700,
            color: '#27272a',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {current?.Name || 'Wybierz listę'}
          <svg
            width="10"
            height="10"
            viewBox="0 0 12 12"
            style={{ opacity: 0.5, flexShrink: 0 }}
          >
            <path
              d="M2 4l4 4 4-4"
              stroke="#27272a"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {open && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: '#fff',
              border: '1px solid #e4e4e7',
              borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              minWidth: 280,
              zIndex: 20,
              padding: 4,
            }}
          >
            {safeList.map((l) => (
              <button
                key={l.ID}
                onClick={() => {
                  onSelect(l.ID);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  textAlign: 'left',
                  background: l.ID === selectedId ? '#fef9c3' : 'transparent',
                  border: 'none',
                  padding: '8px 10px',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <span style={{ flex: 1 }}>{l.Name}</span>
                <span style={{ fontSize: 11, color: '#71717a', fontWeight: 500 }}>
                  {l.EntryCount} zaw.
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Class badge ───────────────────────────────────────────────────────────────
function ClassBadge({ cls, color }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 10,
        fontWeight: 800,
        color: '#fff',
        background: color || '#52525b',
        padding: '2px 6px',
        borderRadius: 4,
        letterSpacing: 0.3,
      }}
    >
      {cls}
    </span>
  );
}

// ── Desktop table ─────────────────────────────────────────────────────────────
function DesktopStartList({ entries, classColors }) {
  return (
    <div style={{ padding: '0 24px 32px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '60px 1fr 1.5fr 1.3fr 1.4fr 100px',
          padding: '12px 14px',
          fontSize: 10,
          color: '#71717a',
          fontWeight: 700,
          letterSpacing: 0.5,
          borderBottom: '1px solid #e4e4e7',
        }}
      >
        <span>NUMER</span>
        <span>ZAWODNIK</span>
        <span>ZAŁOGA</span>
        <span>KLUBY</span>
        <span>SAMOCHÓD I KLASA</span>
        <span style={{ textAlign: 'right' }}>GODZ. STARTU</span>
      </div>

      {entries.map((r, idx) => (
        <div
          key={idx}
          style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 1.5fr 1.3fr 1.4fr 100px',
            padding: '14px',
            alignItems: 'center',
            background: idx % 2 === 0 ? '#fff' : '#fafafa',
            borderBottom: '1px solid #f4f4f5',
          }}
        >
          <span>
            <span
              style={{
                background: '#27272a',
                color: '#fff',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 800,
                padding: '5px 10px',
                display: 'inline-block',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              #{r.PositionNumber}
            </span>
          </span>

          <div style={{ fontSize: 12, lineHeight: 1.4, color: '#52525b' }}>
            {r.Zawodnik}
          </div>

          <div style={{ fontSize: 13, lineHeight: 1.4 }}>
            <div style={{ fontWeight: 700 }}>{r.Kierowca}</div>
            <div style={{ color: '#52525b', fontWeight: 500 }}>{r.Pilot}</div>
          </div>

          <div style={{ fontSize: 12, lineHeight: 1.4, color: '#52525b' }}>
            <div>{r.DriverClub}</div>
            <div style={{ color: '#71717a' }}>{r.CODriverClub}</div>
          </div>

          <div style={{ fontSize: 13, lineHeight: 1.4 }}>
            <div style={{ fontWeight: 600 }}>{r.Samochod}</div>
            {r.Klasa && (
              <div style={{ marginTop: 3 }}>
                <ClassBadge cls={r.Klasa} color={classColors[r.Klasa]} />
              </div>
            )}
          </div>

          <span
            style={{
              textAlign: 'right',
              fontSize: 14,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              color: r.StartTime ? '#27272a' : '#a1a1aa',
            }}
          >
            {r.StartTime || '—'}
          </span>
        </div>
      ))}

      {entries.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#71717a', fontSize: 13 }}>
          Brak zawodników na tej liście.
        </div>
      )}
    </div>
  );
}

// ── Mobile cards ──────────────────────────────────────────────────────────────
const MOBILE_GRID = '44px 1fr 1.1fr 1.1fr 50px';

function MobileStartList({ entries, classColors }) {
  return (
    <div style={{ padding: '0 0 32px' }}>
      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: MOBILE_GRID,
        padding: '8px 12px',
        fontSize: 9,
        fontWeight: 700,
        color: '#71717a',
        letterSpacing: 0.5,
        borderBottom: '1px solid #e4e4e7',
        gap: 4,
      }}>
        <span>NR</span>
        <span>ZAWODNIK</span>
        <span>KIEROWCA / PILOT</span>
        <span>SAMOCHÓD</span>
        <span style={{ textAlign: 'right' }}>GODZ.</span>
      </div>

      {entries.map((r, idx) => (
        <div
          key={idx}
          style={{
            display: 'grid',
            gridTemplateColumns: MOBILE_GRID,
            padding: '10px 12px',
            alignItems: 'center',
            gap: 4,
            background: idx % 2 === 0 ? '#fff' : '#fafafa',
            borderBottom: '1px solid #f4f4f5',
          }}
        >
          <span>
            <span style={{
              background: '#27272a',
              color: '#fff',
              borderRadius: 5,
              fontSize: 12,
              fontWeight: 800,
              padding: '3px 6px',
              display: 'inline-block',
              fontVariantNumeric: 'tabular-nums',
            }}>
              #{r.PositionNumber}
            </span>
          </span>

          <div style={{ fontSize: 11, lineHeight: 1.35, fontWeight: 600, wordBreak: 'break-word' }}>
            {r.Zawodnik || '—'}
          </div>

          <div style={{ fontSize: 11, lineHeight: 1.35 }}>
            <div style={{ fontWeight: 600 }}>{r.Kierowca}</div>
            <div style={{ color: '#52525b' }}>{r.Pilot}</div>
          </div>

          <div style={{ fontSize: 11, lineHeight: 1.35 }}>
            <div style={{ color: '#52525b' }}>{r.Samochod}</div>
            {r.Klasa && (
              <div style={{ marginTop: 2 }}>
                <ClassBadge cls={r.Klasa} color={classColors[r.Klasa]} />
              </div>
            )}
          </div>

          <span style={{
            textAlign: 'right',
            fontSize: 12,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: r.StartTime ? '#27272a' : '#a1a1aa',
          }}>
            {r.StartTime || '—'}
          </span>
        </div>
      ))}

      {entries.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#71717a', fontSize: 13 }}>
          Brak zawodników na tej liście.
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const StartListPage = () => {
  const { EventID } = useParams();
  const numericEventID = EventID.replace('event-', '');
  const isMobile = useMobileCheck();

  const [allLists, setAllLists] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(null);
  const [selectedListId, setSelectedListId] = useState(null);
  const [listDetail, setListDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch all published lists for this event
  useEffect(() => {
    const fetchLists = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/public/eventStartLists?EventID=${numericEventID}`
        );
        const data = await res.json();
        const safeData = Array.isArray(data) ? data : [];
        setAllLists(safeData);

        // Build unique competitions with list counts
        const compMap = {};
        safeData.forEach((l) => {
          if (!compMap[l.CompetitionID]) {
            compMap[l.CompetitionID] = { ID: l.CompetitionID, Name: l.CompetitionName, listCount: 0 };
          }
          compMap[l.CompetitionID].listCount++;
        });
        const comps = Object.values(compMap);
        setCompetitions(comps);

        if (comps.length > 0) {
          const firstCompId = comps[0].ID;
          setSelectedCompetitionId(firstCompId);
          const listsForFirstComp = safeData.filter((l) => l.CompetitionID === firstCompId);
          const newest = listsForFirstComp.reduce((a, b) =>
            new Date(b.UpdatedAt) > new Date(a.UpdatedAt) ? b : a
          , listsForFirstComp[0]);
          if (newest) setSelectedListId(newest.ID);
        }
      } catch (err) {
        console.error('Error fetching start lists:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLists();
  }, [numericEventID]);

  // Fetch list detail when selection changes
  useEffect(() => {
    if (!selectedListId) return;

    const fetchDetail = async () => {
      setDetailLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/public/startListDetail/${selectedListId}`);
        const data = await res.json();
        setListDetail(data);
      } catch (err) {
        console.error('Error fetching start list detail:', err);
      } finally {
        setDetailLoading(false);
      }
    };

    fetchDetail();
  }, [selectedListId]);

  const filteredLists = selectedCompetitionId == null
    ? allLists
    : allLists.filter((l) => l.CompetitionID === selectedCompetitionId);

  const handleChipChange = (compId) => {
    setSelectedCompetitionId(compId);
    const listsForComp = allLists.filter((l) => l.CompetitionID === compId);
    if (listsForComp.length > 0) {
      const newest = listsForComp.reduce((a, b) =>
        new Date(b.UpdatedAt) > new Date(a.UpdatedAt) ? b : a
      , listsForComp[0]);
      setSelectedListId(newest.ID);
    }
  };

  const entries = Array.isArray(listDetail?.entries) ? listDetail.entries : [];
  const classColors = buildClassColors(entries);

  return (
    <div className="container-bkg">
      <Navbar EventID={numericEventID} />
      <div
        style={{
          background: '#fff',
          minHeight: '80vh',
          fontFamily: '-apple-system, "SF Pro", Inter, system-ui, sans-serif',
          color: '#27272a',
        }}
      >
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#71717a', fontSize: 13 }}>
            Ładowanie list startowych…
          </div>
        ) : allLists.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#71717a', fontSize: 13 }}>
            Brak opublikowanych list startowych dla tego wydarzenia.
          </div>
        ) : (
          <>
            <CompetitionChips
              competitions={competitions}
              selected={selectedCompetitionId}
              onChange={handleChipChange}
            />

            <ListDropdown
              lists={filteredLists}
              selectedId={selectedListId}
              onSelect={setSelectedListId}
            />

            {detailLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#71717a', fontSize: 13 }}>
                Ładowanie…
              </div>
            ) : isMobile ? (
              <MobileStartList entries={entries} classColors={classColors} />
            ) : (
              <DesktopStartList entries={entries} classColors={classColors} />
            )}
          </>
        )}
      </div>
      <div className="section">
        <FooterPart />
      </div>
    </div>
  );
};

export default StartListPage;
